/**
 * Self-test suite: Ed25519 sender signatures.
 *
 * Exercises `SenderSignatures.ts` end-to-end.  Zero external network calls;
 * all crypto via SubtleCrypto in the current browser/Node runtime.
 *
 * Registered with `isVerified: true` — verified against Chrome on Windows
 * 2026-05-21 and promoted to `[x]` in `Signal_standard_web.md`. Means every
 * outbound envelope's Ed25519 sign + verify round-trip + tamper / wrong-key /
 * algorithm-guard rejection paths all pass; a regression now throws on dev-
 * server startup.
 */

// Side-effect import that populates the runner registry via Vite glob.
import "../selftest";

import {
  SelfTestRunner,
  buildSuiteResult,
  pass,
  fail,
} from "../selftest";
import {
  canonicalBytes,
  generateKeyPair,
  importPrivateKey,
  importPublicKey,
  signEnvelope,
  verifyEnvelope,
  verifyEnvelopeOrThrow,
  SignatureVerificationError,
  SignatureAlgorithmError,
  ED25519_SIG_BYTES,
  ED25519_PUB_KEY_BYTES,
  type EnvelopeFields,
} from "./SenderSignatures";

// ─── helpers ──────────────────────────────────────────────────────────────────

/** Produce a minimal valid EnvelopeFields for tests. */
function makeFields(
  override: Partial<EnvelopeFields> = {},
): EnvelopeFields {
  return {
    sender: { userId: "alice", deviceId: 1 },
    recipient: { userId: "bob", deviceId: 2 },
    ciphertext: new Uint8Array([0xde, 0xad, 0xbe, 0xef]),
    aad: new Uint8Array([0x01, 0x02]),
    ...override,
  };
}

// ─── suite ────────────────────────────────────────────────────────────────────

SelfTestRunner.register({
  name: "Sender signatures (Ed25519)",
  isVerified: true,

  run: async () => {
    // ── 1. Sign + verify round-trip ──────────────────────────────────────────
    const case1 = await (async () => {
      const name = "sign + verify round-trip";
      try {
        const kp = await generateKeyPair();
        const fields = makeFields();
        const sig = await signEnvelope(fields, kp.privateKey);
        const ok = await verifyEnvelope(fields, sig, kp.publicKey);
        if (!ok)
          return fail(name, "verifyEnvelope returned false for a just-signed envelope");
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 2. Wrong public key returns false ────────────────────────────────────
    const case2 = await (async () => {
      const name = "wrong public key returns false";
      try {
        const kp1 = await generateKeyPair();
        const kp2 = await generateKeyPair();
        const fields = makeFields();
        const sig = await signEnvelope(fields, kp1.privateKey);
        const ok = await verifyEnvelope(fields, sig, kp2.publicKey);
        if (ok)
          return fail(name, "verifyEnvelope returned true with a different keypair's public key");
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 3. Tampered message returns false ────────────────────────────────────
    const case3 = await (async () => {
      const name = "tampered ciphertext returns false";
      try {
        const kp = await generateKeyPair();
        const fields = makeFields();
        const sig = await signEnvelope(fields, kp.privateKey);

        // Flip one byte in the ciphertext
        const tampered = new Uint8Array(fields.ciphertext);
        tampered[0] ^= 0xff;
        const tamperedFields: EnvelopeFields = { ...fields, ciphertext: tampered };

        const ok = await verifyEnvelope(tamperedFields, sig, kp.publicKey);
        if (ok)
          return fail(name, "verifyEnvelope returned true after flipping a ciphertext byte");
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 4. Empty payload ─────────────────────────────────────────────────────
    const case4 = await (async () => {
      const name = "empty payload signs and verifies";
      try {
        const kp = await generateKeyPair();
        const fields = makeFields({
          ciphertext: new Uint8Array(0),
          aad: new Uint8Array(0),
        });
        const sig = await signEnvelope(fields, kp.privateKey);
        if (sig.byteLength !== ED25519_SIG_BYTES)
          return fail(name, `signature length ${sig.byteLength} != ${ED25519_SIG_BYTES}`);
        const ok = await verifyEnvelope(fields, sig, kp.publicKey);
        if (!ok)
          return fail(name, "empty-payload signature failed to verify");

        // Distinct from a non-empty payload
        const nonEmptyFields = makeFields();
        const okCross = await verifyEnvelope(nonEmptyFields, sig, kp.publicKey);
        if (okCross)
          return fail(name, "empty-payload signature verified a non-empty payload");

        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 5. Long payload (1 MB) ───────────────────────────────────────────────
    const case5 = await (async () => {
      const name = "1 MB payload signs and verifies";
      try {
        const kp = await generateKeyPair();
        const big = new Uint8Array(1_048_576);
        // Web Crypto caps `getRandomValues` at 65536 bytes per call — fill in chunks.
        const RAND_CHUNK = 0x10000;
        for (let i = 0; i < big.byteLength; i += RAND_CHUNK) {
          crypto.getRandomValues(
            big.subarray(i, Math.min(i + RAND_CHUNK, big.byteLength)),
          );
        }
        const fields = makeFields({ ciphertext: big });
        const sig = await signEnvelope(fields, kp.privateKey);
        const ok = await verifyEnvelope(fields, sig, kp.publicKey);
        if (!ok)
          return fail(name, "1 MB payload signature failed to verify");
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 6. Signature length is exactly 64 bytes ──────────────────────────────
    const case6 = await (async () => {
      const name = "signature length is exactly 64 bytes";
      try {
        const kp = await generateKeyPair();
        const sig = await signEnvelope(makeFields(), kp.privateKey);
        if (sig.byteLength !== ED25519_SIG_BYTES)
          return fail(
            name,
            `Expected ${ED25519_SIG_BYTES} bytes, got ${sig.byteLength}`,
          );
        if (ED25519_SIG_BYTES !== 64)
          return fail(name, `ED25519_SIG_BYTES constant is ${ED25519_SIG_BYTES}, expected 64`);
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 7. Public key length is exactly 32 bytes ─────────────────────────────
    const case7 = await (async () => {
      const name = "public key length is exactly 32 bytes";
      try {
        const kp = await generateKeyPair();
        if (kp.publicKeyBytes.byteLength !== ED25519_PUB_KEY_BYTES)
          return fail(
            name,
            `Expected ${ED25519_PUB_KEY_BYTES} bytes, got ${kp.publicKeyBytes.byteLength}`,
          );
        if (ED25519_PUB_KEY_BYTES !== 32)
          return fail(name, `ED25519_PUB_KEY_BYTES constant is ${ED25519_PUB_KEY_BYTES}, expected 32`);
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 8. Wrong algorithm: ECDSA-P256 key rejected ──────────────────────────
    // Import a P-256 keypair and try to verify with it using our Ed25519
    // verifier.  It must throw `SignatureAlgorithmError`, not silently return
    // false (that would mask algorithm confusion attacks).
    const case8 = await (async () => {
      const name = "wrong algorithm (ECDSA-P256 key) throws SignatureAlgorithmError";
      try {
        // Generate a real P-256 keypair
        const p256kp = await crypto.subtle.generateKey(
          { name: "ECDSA", namedCurve: "P-256" },
          true,
          ["sign", "verify"],
        );

        // Sign something with P-256
        const p256sig = new Uint8Array(
          await crypto.subtle.sign(
            { name: "ECDSA", hash: "SHA-256" },
            p256kp.privateKey,
            new Uint8Array([1, 2, 3]),
          ),
        );

        // Now pass the P-256 public key into our Ed25519 verifier.
        // WebCrypto must throw because the key algorithm doesn't match.
        let caught: unknown = null;
        try {
          await verifyEnvelope(makeFields(), p256sig, p256kp.publicKey);
        } catch (e) {
          caught = e;
        }

        if (caught === null)
          return fail(name, "verifyEnvelope did not throw when given a P-256 public key");

        if (!(caught instanceof SignatureAlgorithmError))
          return fail(
            name,
            `Expected SignatureAlgorithmError, got ${(caught as Error)?.name ?? String(caught)}`,
          );

        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 9. verifyEnvelopeOrThrow: throws SignatureVerificationError on bad sig ─
    const case9 = await (async () => {
      const name = "verifyEnvelopeOrThrow throws SignatureVerificationError on bad sig";
      try {
        const kp1 = await generateKeyPair();
        const kp2 = await generateKeyPair();
        const fields = makeFields();
        const sig = await signEnvelope(fields, kp1.privateKey);

        let caught: unknown = null;
        try {
          await verifyEnvelopeOrThrow(fields, sig, kp2.publicKey);
        } catch (e) {
          caught = e;
        }

        if (caught === null)
          return fail(name, "verifyEnvelopeOrThrow did not throw on wrong key");

        if (!(caught instanceof SignatureVerificationError))
          return fail(
            name,
            `Expected SignatureVerificationError, got ${(caught as Error)?.name ?? String(caught)}`,
          );

        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 10. canonicalBytes is deterministic ───────────────────────────────────
    const case10 = await (async () => {
      const name = "canonicalBytes is deterministic (same input → same bytes)";
      try {
        const fields = makeFields();
        const a = canonicalBytes(fields);
        const b = canonicalBytes(fields);
        if (a.byteLength !== b.byteLength)
          return fail(name, `lengths differ: ${a.byteLength} vs ${b.byteLength}`);
        for (let i = 0; i < a.byteLength; i++) {
          if (a[i] !== b[i])
            return fail(name, `byte ${i} differs: ${a[i]} vs ${b[i]}`);
        }
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 11. canonicalBytes differs when senderUserId changes ─────────────────
    const case11 = await (async () => {
      const name = "canonicalBytes is sensitive to senderUserId";
      try {
        const a = canonicalBytes(makeFields({ sender: { userId: "alice", deviceId: 1 } }));
        const b = canonicalBytes(makeFields({ sender: { userId: "ALICE", deviceId: 1 } }));
        const equal = a.byteLength === b.byteLength && a.every((v, i) => v === b[i]);
        if (equal)
          return fail(name, "canonical bytes did not change when senderUserId changed");
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    // ── 12. importPublicKey rejects wrong-length raw bytes ────────────────────
    const case12 = await (async () => {
      const name = "importPublicKey rejects non-32-byte input";
      try {
        let caught: unknown = null;
        try {
          await importPublicKey(new Uint8Array(31));
        } catch (e) {
          caught = e;
        }
        if (caught === null)
          return fail(name, "importPublicKey did not throw on 31-byte input");
        if (!(caught instanceof SignatureAlgorithmError))
          return fail(
            name,
            `Expected SignatureAlgorithmError, got ${(caught as Error)?.name ?? String(caught)}`,
          );
        return pass(name);
      } catch (e) {
        return fail(name, String(e));
      }
    })();

    return buildSuiteResult([
      case1, case2, case3, case4, case5, case6, case7,
      case8, case9, case10, case11, case12,
    ]);
  },
});
